import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { safeText } from '../lib/render';
import Wizard, { FloatingAction } from '../components/Wizard';

const MAX_WORD_COUNT = 3000;
const LEVELS = ['high_school', 'undergraduate', 'postgraduate', 'doctoral'];
const STYLES = ['apa7', 'mla', 'chicago', 'harvard'];

export default function Assignments() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [err, setErr] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardBusy, setWizardBusy] = useState(false);
  const [wizardErr, setWizardErr] = useState(null);

  async function loadHistory() {
    try {
      const res = await api.get('/api/assignment/history');
      setList(res.assignments || []);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to load history');
    }
  }

  useEffect(() => { void loadHistory(); }, []);

  async function submit(state) {
    setWizardBusy(true);
    setWizardErr(null);
    try {
      const res = await api.post('/api/assignment/generate', {
        assignment_type: state.assignmentType,
        topic: (state.topic || '').trim(),
        instructions: (state.instructions || '').trim(),
        word_count: state.wordCount,
        academic_level: state.academicLevel,
        referencing_style: state.referencingStyle,
      });
      if (res.assignment_id) {
        nav(`/assignment/${res.assignment_id}/live`);
      } else {
        setWizardErr(res.error || 'Failed to start assignment');
      }
    } catch (e) {
      setWizardErr(e instanceof ApiError ? e.message : 'Failed to start assignment');
    } finally {
      setWizardBusy(false);
    }
  }

  return (
    <>
      <section className="hero-gradient py-5">
        <div className="container py-4">
          <div className="text-center text-white">
            <span className="badge bg-info text-dark px-3 py-2 mb-3" style={{ fontSize: '0.82rem' }}>
              <i className="fas fa-file-alt me-1"></i>Short assignments
            </span>
            <h1 style={{ fontWeight: 800, fontSize: '2.6rem' }}>Assignments</h1>
            <p className="lead" style={{ color: 'rgba(255,255,255,0.75)', maxWidth: 640, margin: '0 auto' }}>
              Essays up to 3,000 words, properly referenced. 1 credit each.
            </p>
          </div>
        </div>
      </section>

      <div className="main-container">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            {err && <div className="alert alert-danger">{err}</div>}

            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <h4 className="mb-0">
                <i className="fas fa-history me-2" style={{ color: 'var(--primary)' }}></i>
                Your assignments
              </h4>
              <button
                type="button"
                className="btn btn-cta"
                onClick={() => { setWizardErr(null); setWizardOpen(true); }}
              >
                <i className="fas fa-plus me-1"></i>New assignment
              </button>
            </div>

            {list.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                  <h5>No assignments yet</h5>
                  <p className="text-muted mb-3" style={{ maxWidth: 520, margin: '0 auto' }}>
                    Click <strong>New assignment</strong> to generate your first essay
                    — we'll walk you through it step by step.
                  </p>
                  <button type="button" className="btn btn-cta btn-lg" onClick={() => setWizardOpen(true)}>
                    <i className="fas fa-plus me-1"></i>Start your first assignment
                  </button>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body">
                  <div className="list-group list-group-flush">
                    {list.map((a) => (
                      <AssignmentRow
                        key={a.assignment_id}
                        a={a}
                        onRetried={loadHistory}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating entry point for wider screens */}
      <FloatingAction label="New assignment" onClick={() => setWizardOpen(true)} />

      {wizardOpen && (
        <Wizard
          title="New assignment"
          busy={wizardBusy}
          error={wizardErr}
          onClose={() => !wizardBusy && setWizardOpen(false)}
          onSubmit={submit}
          submitLabel="Generate (1 credit)"
          initialState={{
            assignmentType: 'Essay',
            wordCount: 1500,
            topic: '',
            instructions: '',
            academicLevel: 'undergraduate',
            referencingStyle: 'apa7',
          }}
          steps={[
            {
              label: 'Type',
              canAdvance: (s) => !!s.assignmentType,
              render: TypeStep,
            },
            {
              label: 'Length',
              canAdvance: (s) => s.wordCount >= 500 && s.wordCount <= MAX_WORD_COUNT,
              render: LengthStep,
            },
            {
              label: 'Question',
              canAdvance: (s) => (s.topic || '').trim().length >= 10,
              render: QuestionStep,
            },
            {
              label: 'Style',
              canAdvance: (s) => !!s.academicLevel && !!s.referencingStyle,
              render: StyleStep,
            },
            {
              label: 'Review',
              canAdvance: () => true,
              render: ReviewStep,
            },
          ]}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// List row with inline Retry button for failed assignments.
// ────────────────────────────────────────────────────────────────

function AssignmentRow({ a, onRetried }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  const status = String(a.status || 'pending').toLowerCase();
  const failed = status === 'failed' || status === 'error';
  const done = status === 'completed';
  const to = done
    ? `/assignment/${a.assignment_id}`
    : `/assignment/${a.assignment_id}/live`;

  async function retry(e) {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post(`/api/assignment/${a.assignment_id}/retry`, {});
      if (res?.success) {
        if (onRetried) await onRetried();
        nav(`/assignment/${a.assignment_id}/live`);
      } else {
        setErr(res?.error || 'Retry failed');
      }
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : 'Retry failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Link
      to={to}
      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center flex-wrap gap-2"
      style={{ border: 'none', borderBottom: '1px solid var(--border)', padding: '1rem 0' }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">{safeText(a.topic, '(untitled)')}</div>
        <div className="text-muted small mt-1">
          {safeText(a.assignment_type, 'Essay')}
          {a.created_at && (<> • {new Date(a.created_at).toLocaleDateString()}</>)}
        </div>
        {err && <div className="text-danger small mt-1">{err}</div>}
      </div>
      <div className="d-flex align-items-center gap-2">
        <span className={`status-badge ${status}`}>{status}</span>
        {failed && (
          <button
            type="button"
            className="btn btn-sm btn-warning"
            onClick={retry}
            disabled={busy}
            title="Re-run generation — free, this one was on us"
          >
            <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-redo'} me-1`}></i>
            {busy ? 'Retrying…' : 'Retry (free)'}
          </button>
        )}
      </div>
    </Link>
  );
}

// ────────────────────────────────────────────────────────────────
// Each step is its own small component — do not combine them.
// ────────────────────────────────────────────────────────────────

function TypeStep({ state }) {
  return (
    <div>
      <h5 className="mb-2">What kind of assignment?</h5>
      <p className="text-muted small mb-4">
        Only essays are supported for now — we're focused on producing
        high-quality short-form essays up to 3,000 words.
      </p>
      <div
        style={{
          border: '2px solid var(--primary)',
          borderRadius: 12,
          padding: '1.1rem 1.3rem',
          background: 'rgba(79,70,229,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <div
          style={{
            width: 44, height: 44, borderRadius: 10,
            background: 'var(--primary)', color: '#fff',
            display: 'grid', placeItems: 'center',
          }}
        >
          <i className="fas fa-pen-fancy"></i>
        </div>
        <div>
          <div className="fw-bold">{state.assignmentType}</div>
          <div className="text-muted small">
            Standard academic essay with in-text citations and a References list.
          </div>
        </div>
      </div>
    </div>
  );
}

function LengthStep({ state, setState }) {
  const preset = [500, 1000, 1500, 2000, 2500, 3000];
  return (
    <div>
      <h5 className="mb-2">How long should it be?</h5>
      <p className="text-muted small mb-4">
        Pick a target word count. The maximum for a single assignment is{' '}
        <strong>{MAX_WORD_COUNT.toLocaleString()} words</strong>.
      </p>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {preset.map((p) => (
          <button
            key={p}
            type="button"
            className={`btn ${state.wordCount === p ? 'btn-cta' : 'btn-outline-secondary'}`}
            onClick={() => setState({ ...state, wordCount: p })}
          >
            {p.toLocaleString()} words
          </button>
        ))}
      </div>
      <label className="form-label small text-muted">Or enter a custom number (500–{MAX_WORD_COUNT}):</label>
      <input
        type="number"
        className="form-control"
        min={500}
        max={MAX_WORD_COUNT}
        step={100}
        value={state.wordCount}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isFinite(v)) return;
          setState({ ...state, wordCount: Math.max(500, Math.min(MAX_WORD_COUNT, v)) });
        }}
      />
    </div>
  );
}

function QuestionStep({ state, setState }) {
  return (
    <div>
      <h5 className="mb-2">What's the question or topic?</h5>
      <p className="text-muted small mb-3">
        Paste the exact question from your lecturer. More detail = better output —
        include any scenario, required framework, or specific data points to engage with.
      </p>
      <label className="form-label fw-semibold">Topic / question</label>
      <textarea
        className="form-control mb-3"
        rows={4}
        value={state.topic}
        onChange={(e) => setState({ ...state, topic: e.target.value })}
        placeholder="e.g. Analyse how Herzberg's two-factor theory applies to SME retention strategies in Lusaka, using at least two empirical studies from 2020–2024."
      />
      <label className="form-label fw-semibold small text-muted">
        Extra instructions (optional)
      </label>
      <textarea
        className="form-control"
        rows={2}
        value={state.instructions}
        onChange={(e) => setState({ ...state, instructions: e.target.value })}
        placeholder="Any specific angle, required sections, or constraints."
      />
    </div>
  );
}

function StyleStep({ state, setState }) {
  return (
    <div>
      <h5 className="mb-3">Academic level & referencing</h5>
      <div className="mb-3">
        <label className="form-label fw-semibold">Academic level</label>
        <select
          className="form-select"
          value={state.academicLevel}
          onChange={(e) => setState({ ...state, academicLevel: e.target.value })}
        >
          {LEVELS.map((l) => (
            <option key={l} value={l}>{l.replace('_', ' ')}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="form-label fw-semibold">Referencing style</label>
        <select
          className="form-select"
          value={state.referencingStyle}
          onChange={(e) => setState({ ...state, referencingStyle: e.target.value })}
        >
          {STYLES.map((s) => (
            <option key={s} value={s}>{s.toUpperCase()}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function ReviewStep({ state }) {
  return (
    <div>
      <h5 className="mb-3">Ready to generate</h5>
      <div className="card" style={{ background: '#fafbfd', border: '1px solid var(--border)' }}>
        <div className="card-body">
          <ReviewRow label="Type" value={state.assignmentType} />
          <ReviewRow label="Word count" value={`${Number(state.wordCount).toLocaleString()} words`} />
          <ReviewRow label="Topic" value={state.topic} wrap />
          {state.instructions && <ReviewRow label="Instructions" value={state.instructions} wrap />}
          <ReviewRow label="Academic level" value={String(state.academicLevel).replace('_', ' ')} />
          <ReviewRow label="Referencing" value={String(state.referencingStyle).toUpperCase()} />
        </div>
      </div>
      <div className="alert alert-info small mt-3 mb-0">
        <i className="fas fa-info-circle me-2"></i>
        Generation runs in the background. You can close the page — your
        assignment will be waiting in the list when it's done.
      </div>
    </div>
  );
}

function ReviewRow({ label, value, wrap }) {
  return (
    <div className="d-flex gap-3 py-1" style={{ borderBottom: '1px dashed rgba(0,0,0,0.06)' }}>
      <div className="text-muted small" style={{ minWidth: 120 }}>{label}</div>
      <div
        className="small"
        style={{
          flex: 1,
          wordBreak: wrap ? 'break-word' : 'normal',
          whiteSpace: wrap ? 'normal' : 'nowrap',
          overflow: wrap ? 'visible' : 'hidden',
          textOverflow: wrap ? 'clip' : 'ellipsis',
          fontWeight: 500,
        }}
      >
        {value || <span className="text-muted">—</span>}
      </div>
    </div>
  );
}
